import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormGroup, Form, Input, Label, Button, Row, Col } from "reactstrap";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import ValetApi from "../api/Api";

import "./ValetRegisterForm.css";

// Validation schema
const validationSchema = Yup.object({
    username: Yup.string().required("Username required"),
    password: Yup.string().min(6, "Password must be at least 6 characters").required("Password required"),
    first_name: Yup.string().required("First name required"),
    last_name: Yup.string().required("Last name required"),
    email: Yup.string().email("Invalid email").required("Email required"),
    phone: Yup.string().required("Phone required"),
    inviteCode: Yup.string().required("Invite code required"),
});

const ValetRegisterForm = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search || "");
    const prefilledEmail = String(params.get("email") || "")
        .trim()
        .toLowerCase();
    const prefilledInviteCode = String(params.get("inviteCode") || "").trim();
    const isEmailLocked = Boolean(prefilledEmail);
    const isInviteCodeLocked = Boolean(prefilledInviteCode);

    const formik = useFormik({
        initialValues: {
            username: "",
            password: "",
            first_name: "",
            last_name: "",
            email: prefilledEmail,
            phone: "",
            inviteCode: prefilledInviteCode,
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values, { resetForm, setSubmitting }) => {
            const payload = {
                ...values,
                username: values.username.trim().toLowerCase(),
                email: values.email.trim().toLowerCase(),
                inviteCode: values.inviteCode.trim(),
            };
            try {
                await ValetApi.registerUserWithInvite(payload);
                toast.success("Registration successful! Await admin approval.");
                resetForm();
            } catch {
                toast.error("Registration failed. Check your invite code.");
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Helper render functions for each field
    const renderInputField = (name, type = "text", autoComplete = "", options = {}) => (
        <Input
            id={name}
            name={name}
            type={type}
            placeholder={name.replace(/_/g, " ")}
            value={formik.values[name]}
            autoComplete={autoComplete}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            readOnly={Boolean(options.readOnly)}
            invalid={formik.touched[name] && !!formik.errors[name]}
        />
    );

    return (
        <Row className="justify-content-center valet-register-row">
            <Col
                xs={12}
                md={6}
                className="valet-register-col">
                <Form
                    onSubmit={formik.handleSubmit}
                    className="valet-register-form">
                    <h1 className="valet-register-title">Valet Registration</h1>
                    <FormGroup>
                        <Label htmlFor="username">Username</Label>
                        {renderInputField("username")}
                        {formik.touched.username && formik.errors.username && <div className="text-danger">{formik.errors.username}</div>}
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="password">Password</Label>
                        {renderInputField("password", "password", "new-password")}
                        {formik.touched.password && formik.errors.password && <div className="text-danger">{formik.errors.password}</div>}
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="first_name">First Name</Label>
                        {renderInputField("first_name")}
                        {formik.touched.first_name && formik.errors.first_name && <div className="text-danger">{formik.errors.first_name}</div>}
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="last_name">Last Name</Label>
                        {renderInputField("last_name")}
                        {formik.touched.last_name && formik.errors.last_name && <div className="text-danger">{formik.errors.last_name}</div>}
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="email">Email</Label>
                        {renderInputField("email", "email", "email", { readOnly: isEmailLocked })}
                        {isEmailLocked ? <small className="text-muted">Email is locked to this invite.</small> : null}
                        {formik.touched.email && formik.errors.email && <div className="text-danger">{formik.errors.email}</div>}
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="phone">Phone</Label>
                        {renderInputField("phone", "tel")}
                        {formik.touched.phone && formik.errors.phone && <div className="text-danger">{formik.errors.phone}</div>}
                    </FormGroup>
                    <FormGroup>
                        <Label htmlFor="inviteCode">Invite Code</Label>
                        {renderInputField("inviteCode", "text", "", { readOnly: isInviteCodeLocked })}
                        {isInviteCodeLocked ? <small className="text-muted">Invite code is locked from your email link.</small> : null}
                        {formik.touched.inviteCode && formik.errors.inviteCode && <div className="text-danger">{formik.errors.inviteCode}</div>}
                    </FormGroup>
                    <Button
                        color="primary"
                        type="submit"
                        disabled={formik.isSubmitting}
                        className="valet-register-submit">
                        Register
                    </Button>
                </Form>
            </Col>
        </Row>
    );
};

export default ValetRegisterForm;
